import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-chat-modifier.css?inline'
import type { ModifierChoiceHint, ModifierGroup } from './bbcore/types'

export interface ModifierConfirmation {
  itemName: string
  quantity: number
  // Base unit price string (e.g. "R58.00") — forwarded so the optimistic
  // cart patch shows the line priced before the server response lands
  // (Trello #135). Undefined when the hint was synthesised without it.
  price?: string
  selections: Array<{
    group: string
    options: string[]
    groupType?: string
    // Per-option modifier_type, keyed by option name. Lets the cart-add
    // router split a single group into options/extras/removals option-by-
    // option, since one group can mix kinds (e.g. "Add Mayo Inside?" is a
    // groupType='adjustments' group whose mayo option is an 'addition').
    // Group-level routing alone would mis-classify mayo as a removal and
    // the backend would silently drop it (Trello #139).
    optionTypes?: Record<string, string>
    // Carried so listeners can split into options/extras for the
    // direct-add cart endpoint: groupType='removal' → removals,
    // groupType='choice' & minSelections=0 → extras, otherwise → options.
    minSelections: number
  }>
  utterance: string
}

function describeRange(min: number, max: number): string {
  if (min === max) return min === 1 ? 'Pick one' : `Pick ${min}`
  if (min === 0) return max === 1 ? 'Optional' : `Pick up to ${max}`
  if (max === 1) return 'Pick at least one'
  return `Pick ${min}–${max}`
}

export function buildUtterance(
  item: string,
  qty: number,
  selections: Array<{ group: string; options: string[]; groupType?: string }>,
): string {
  const filled = selections.filter((s) => s.options.length > 0)
  const qtyPart = qty > 1 ? `${qty} ${item}s` : `a ${item}`
  if (filled.length === 0) return `Add ${qtyPart}.`
  // Removal groups read as "without X" — otherwise deselecting an
  // ingredient under "Remove Ingredients" composed as "Add … with carrot",
  // i.e. the opposite of what the customer asked (Trello #112).
  const phrase = (s: { options: string[] }) =>
    s.options.length === 1 ? s.options[0] : s.options.join(' and ')
  const additions = filled
    .filter((s) => s.groupType !== 'removal')
    .map(phrase)
  const removals = filled
    .filter((s) => s.groupType === 'removal')
    .map(phrase)
  const clauses = [
    additions.length ? `with ${additions.join(', ')}` : null,
    removals.length ? `without ${removals.join(', ')}` : null,
  ].filter(Boolean)
  return `Add ${qtyPart} ${clauses.join(', ')}.`
}

@customElement('bb-chat-modifier')
export class BbChatModifier extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false })
  hint: ModifierChoiceHint | null = null

  // Frozen view: the hint has been superseded by a newer turn. Keeps the picked
  // selections on screen but blocks any further interaction.
  @property({ type: Boolean, reflect: true })
  disabled = false

  @state() private selections: Record<string, string[]> = {}

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('hint')) {
      // Reset on a new modifier hint (different id).
      this.selections = {}
    }
  }

  private isPicked(group: string, option: string): boolean {
    return (this.selections[group] ?? []).includes(option)
  }

  private toggle(group: ModifierGroup, option: string) {
    if (this.disabled) return
    const current = this.selections[group.name] ?? []
    const has = current.includes(option)
    let next: string[]
    if (group.max_selections === 1) {
      next = has ? [] : [option]
    } else if (has) {
      next = current.filter((o) => o !== option)
    } else if (current.length < group.max_selections) {
      next = [...current, option]
    } else {
      // At max — toggle the oldest off, add the new one.
      next = [...current.slice(1), option]
    }
    this.selections = { ...this.selections, [group.name]: next }
  }

  private isValid(): boolean {
    if (!this.hint) return false
    return this.hint.groups.every((g) => {
      const picked = (this.selections[g.name] ?? []).length
      return picked >= g.min_selections && picked <= g.max_selections
    })
  }

  private confirm() {
    if (this.disabled || !this.hint || !this.isValid()) return
    const selections = this.hint.groups.map((g) => {
      const picked = this.selections[g.name] ?? []
      const optionTypes = Object.fromEntries(
        g.options
          .filter((o) => o.modifier_type && picked.includes(o.name))
          .map((o) => [o.name, o.modifier_type as string]),
      )
      return {
        group: g.name,
        groupType: g.group_type,
        minSelections: g.min_selections,
        options: picked,
        ...(Object.keys(optionTypes).length ? { optionTypes } : {}),
      }
    })
    const detail: ModifierConfirmation = {
      itemName: this.hint.context.item_name,
      quantity: this.hint.context.quantity,
      ...(this.hint.context.price ? { price: this.hint.context.price } : {}),
      selections,
      utterance: buildUtterance(
        this.hint.context.item_name,
        this.hint.context.quantity,
        selections,
      ),
    }
    this.emitAffordance('modifier-confirm', {
      item: this.hint.context.item_name,
      selections: selections.map((s) => `${s.group}:${s.options.join(',')}`),
    })
    this.dispatchEvent(
      new CustomEvent('modifier-confirmed', {
        detail,
        bubbles: true,
        composed: true,
      }),
    )
  }

  private cancel() {
    this.emitAffordance('modifier-cancel', {
      item: this.hint?.context.item_name,
    })
    this.dispatchEvent(
      new CustomEvent('modifier-cancelled', {
        bubbles: true,
        composed: true,
      }),
    )
  }

  private emitAffordance(
    kind: string,
    context?: Record<string, unknown>,
  ) {
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind, ...(context ? { context } : {}) },
        bubbles: true,
        composed: true,
      }),
    )
  }

  render() {
    if (!this.hint) return nothing
    const { context, groups } = this.hint
    const priceLabel =
      context.price_cents > 0
        ? `${(context.price_cents / 100).toFixed(2)} R`
        : ''
    return html`
      <div class="head">
        <span class="item-name">
          ${context.quantity > 1 ? `${context.quantity}× ` : ''}${context.item_name}
        </span>
        ${priceLabel ? html`<span class="price">${priceLabel}</span>` : nothing}
      </div>
      ${groups.map(
        (g) => html`
          <fieldset>
            <legend>
              ${g.name}
              <span class="hint"
                >${describeRange(g.min_selections, g.max_selections)}</span
              >
            </legend>
            <div class="options">
              ${g.options.map((opt) => {
                const checked = this.isPicked(g.name, opt.name)
                const optional = g.min_selections === 0
                // Per-option `modifier_type` (BerryCore item-level field)
                // wins over the group's `group_type`: an "adjustments"
                // group can carry an "addition" option (e.g. Add Mayo) —
                // we must default that to ✕ (not added), not ✓ (kept).
                // Fall back to group-level when the option type is absent.
                const removal = opt.modifier_type
                  ? opt.modifier_type === 'removal'
                  : g.group_type === 'removal'
                // Glyph shows the *resulting* state: for removable items
                // the default is ✓ (kept), clicking flips to ✕ (will be
                // removed); for optional adds the default is ✕ (not in),
                // clicking flips to ✓ (added).
                const present = removal ? !checked : checked
                const glyph = optional ? (present ? '✓' : '✕') : null
                return html`
                  <label class=${checked ? 'checked' : ''}>
                    <input
                      type=${g.max_selections === 1 && g.min_selections > 0
                        ? 'radio'
                        : 'checkbox'}
                      name=${g.name}
                      .checked=${checked}
                      ?disabled=${this.disabled}
                      @change=${() => this.toggle(g, opt.name)}
                    />
                    ${glyph
                      ? html`<span
                          class="opt-glyph opt-glyph--${present
                            ? 'add'
                            : 'remove'}"
                          aria-hidden="true"
                          >${glyph}</span
                        >`
                      : nothing}
                    ${opt.name}
                    ${opt.price_cents > 0
                      ? html`<span class="price-tag"
                          >+${(opt.price_cents / 100).toFixed(2)} R</span
                        >`
                      : nothing}
                  </label>
                `
              })}
            </div>
          </fieldset>
        `,
      )}
      <div class="actions">
        <button class="secondary" ?disabled=${this.disabled} @click=${this.cancel}>
          Cancel
        </button>
        <button
          class="primary"
          ?disabled=${this.disabled || !this.isValid()}
          @click=${this.confirm}
        >
          Add to order
        </button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-chat-modifier': BbChatModifier
  }
}
